import React, { useState, useEffect } from "react";
import { JobFilters as JobFiltersType } from "../types";
import {
  SearchIcon,
  FilterIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "../utils/icons";
import { jobTypes } from "../data/jobs";
import { getUniqueLocations, getAllUniqueTags } from "../data/scraped-jobs";
import LiquidGlassContainer from "./LiquidGlassContainer";
import { refreshAllLiquidGlass } from "../utils/loadLiquidGlass";
import "../styles/JobBoard.css";

interface JobFiltersProps {
  filters: JobFiltersType;
  onFiltersChange: (filters: JobFiltersType) => void;
  totalJobs: number;
  filterOptions?: {
    companies: string[];
    locations: string[];
    tags: string[];
  };
}

const JobFilters: React.FC<JobFiltersProps> = ({
  filters,
  onFiltersChange,
  totalJobs,
  filterOptions,
}) => {
  // State for dynamic data from scraped jobs
  const [locations, setLocations] = useState<string[]>(["All Locations"]);
  const [popularTags, setPopularTags] = useState<string[]>([]);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  // State for location search dropdown
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);

  // Load dynamic data on component mount or when filterOptions change
  useEffect(() => {
    if (filterOptions) {
      // Use filterOptions if provided (from parent component)
      setLocations(filterOptions.locations);
      setPopularTags(filterOptions.tags);
    } else {
      // Fallback to loading data directly
      const loadDynamicData = async () => {
        try {
          const [locationsData, tagsData] = await Promise.all([
            getUniqueLocations(),
            getAllUniqueTags(),
          ]);
          setLocations(locationsData);
          setPopularTags(tagsData);
        } catch (error) {
          console.error("Error loading dynamic filter data:", error);
          // Keep default values
        }
      };

      loadDynamicData();
    }
  }, [filterOptions]);

  // Filter locations based on search input
  useEffect(() => {
    if (locationSearchValue.trim() === "") {
      setFilteredLocations(locations);
    } else {
      const filtered = locations.filter((location) =>
        location.toLowerCase().includes(locationSearchValue.toLowerCase()),
      );
      setFilteredLocations(filtered);
    }
  }, [locationSearchValue, locations]);

  // Initialize location search value from current filter
  useEffect(() => {
    if (filters.location && filters.location !== "All Locations") {
      setLocationSearchValue(filters.location);
    } else {
      setLocationSearchValue("");
    }
  }, [filters.location]);

  useEffect(() => {
    refreshAllLiquidGlass();
  }, [isFiltersCollapsed]);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleLocationSearchChange = (value: string) => {
    setLocationSearchValue(value);
    setIsLocationDropdownOpen(true);

    // Always update the filter with the search value for immediate filtering
    onFiltersChange({ ...filters, location: value.trim() });
  };

  const handleLocationSelect = (location: string) => {
    setLocationSearchValue(location === "All Locations" ? "" : location);
    onFiltersChange({
      ...filters,
      location: location === "All Locations" ? "" : location,
    });
    setIsLocationDropdownOpen(false);
  };

  const handleCompanyChange = (value: string) => {
    onFiltersChange({ ...filters, company: value });
  };

  const handleJobTypeChange = (value: string) => {
    onFiltersChange({ ...filters, jobType: value });
  };

  const handleRemoteChange = (checked: boolean) => {
    onFiltersChange({ ...filters, remote: checked });
  };

  const handleSalaryDisclosedChange = (checked: boolean) => {
    onFiltersChange({ ...filters, salaryDisclosed: checked });
  };

  const handleTagToggle = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: newTags });
  };

  const clearFilters = () => {
    setLocationSearchValue("");
    setIsLocationDropdownOpen(false);
    onFiltersChange({
      search: "",
      location: "",
      company: "",
      jobType: "",
      remote: false,
      tags: [],
      salaryDisclosed: false,
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.location ||
    filters.company ||
    filters.jobType ||
    filters.remote ||
    filters.salaryDisclosed ||
    filters.tags.length > 0;

  return (
    <LiquidGlassContainer borderRadius={8} tintOpacity={0.22}>
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
          className="flex items-center flex-1 cursor-pointer text-left"
        >
          <FilterIcon className="h-5 w-5 text-haig-muted mr-2 shrink-0" />
          <h2 className="text-xl font-bold text-haig-primary tracking-tight">
            Filters
          </h2>
          {isFiltersCollapsed ? (
            <ChevronDownIcon className="h-4 w-4 text-haig-muted ml-2" />
          ) : (
            <ChevronUpIcon className="h-4 w-4 text-haig-muted ml-2" />
          )}
        </button>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-haig-muted">
            {totalJobs} jobs found
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-semibold text-haig-secondary hover:opacity-80 transition-all duration-200"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {!isFiltersCollapsed && (
        <>
          {/* Search */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-haig-text mb-2">
              Search Jobs
            </label>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-haig-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by title, company, or keywords..."
                className="w-full pl-10 pr-4 py-2 border border-haig-border rounded-haig focus:ring-2 focus:ring-haig-secondary focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Location - Searchable Dropdown */}
            <div className="relative">
              <label className="block text-sm font-semibold text-haig-text mb-2">
                Location
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={locationSearchValue}
                  onChange={(e) => handleLocationSearchChange(e.target.value)}
                  onFocus={() => setIsLocationDropdownOpen(true)}
                  onBlur={() => {
                    // Delay closing to allow click on dropdown items
                    setTimeout(() => setIsLocationDropdownOpen(false), 200);
                  }}
                  placeholder="Search locations..."
                  className="w-full px-3 py-2 border border-haig-border rounded-haig focus:ring-2 focus:ring-haig-secondary focus:border-transparent transition-all"
                />
                <ChevronDownIcon
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-haig-muted transition-transform ${
                    isLocationDropdownOpen ? "rotate-180" : ""
                  }`}
                />

                {/* Dropdown */}
                {isLocationDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-haig-background border border-haig-border rounded-haig shadow-haig-card max-h-40 overflow-y-auto">
                    {filteredLocations.length > 0 ? (
                      filteredLocations.map((location) => (
                        <button
                          key={location}
                          type="button"
                          onClick={() => handleLocationSelect(location)}
                          className={`w-full text-left px-3 py-2 hover:bg-haig-surface focus:bg-haig-surface focus:outline-none transition-colors ${
                            filters.location === location ||
                            (location === "All Locations" &&
                              filters.location === "")
                              ? "bg-blue-50 text-haig-secondary font-medium"
                              : "text-haig-text"
                          }`}
                        >
                          {location}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-haig-muted text-sm">
                        No locations found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-semibold text-haig-text mb-2">
                Company
              </label>
              <select
                value={filters.company}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="w-full px-3 py-2 border border-haig-border rounded-haig focus:ring-2 focus:ring-haig-secondary focus:border-transparent bg-haig-background transition-all"
              >
                <option value="">All Companies</option>
                {filterOptions?.companies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>

            {/* Job Type */}
            <div>
              <label className="block text-sm font-semibold text-haig-text mb-2">
                Job Type
              </label>
              <select
                value={filters.jobType}
                onChange={(e) => handleJobTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-haig-border rounded-haig focus:ring-2 focus:ring-haig-secondary focus:border-transparent bg-haig-background transition-all"
              >
                {jobTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Checkboxes Container */}
            <div className="space-y-1">
              {/* Remote Only */}
              <div className="flex items-center" style={{ paddingTop: "25px" }}>
                <input
                  type="checkbox"
                  id="remote"
                  checked={filters.remote}
                  onChange={(e) => handleRemoteChange(e.target.checked)}
                  className="h-4 w-4 text-haig-secondary focus:ring-haig-secondary border-haig-border rounded"
                />
                <label htmlFor="remote" className="ml-2 text-sm text-haig-text">
                  Remote only
                </label>
              </div>

              {/* Salary Disclosed */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="salaryDisclosed"
                  checked={filters.salaryDisclosed}
                  onChange={(e) =>
                    handleSalaryDisclosedChange(e.target.checked)
                  }
                  className="h-4 w-4 text-haig-secondary focus:ring-haig-secondary border-haig-border rounded"
                />
                <label
                  htmlFor="salaryDisclosed"
                  className="ml-2 text-sm text-haig-text"
                >
                  Salary disclosed
                </label>
              </div>
            </div>
          </div>

          {/* Popular Tags */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-haig-text mb-3">
              Popular Skills & Technologies
            </label>
            <div className="flex flex-wrap gap-2">
              {popularTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1 rounded-full text-sm font-semibold transition-all duration-200 ${
                    filters.tags.includes(tag)
                      ? "bg-blue-50 text-haig-secondary border border-haig-border"
                      : "bg-haig-surface text-haig-muted border border-haig-border hover:bg-haig-border/40"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Active Filters Display - Always visible */}
      {hasActiveFilters && (
        <div
          className={`pt-4 border-t border-haig-border ${
            !isFiltersCollapsed ? "mt-6" : "mt-0"
          }`}
        >
          <h3 className="text-sm font-semibold text-haig-text mb-2">
            Active Filters:
          </h3>
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Search: "{filters.search}"
              </span>
            )}
            {filters.location && filters.location !== "All Locations" && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Location: {filters.location}
              </span>
            )}
            {filters.company && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Company: {filters.company}
              </span>
            )}
            {filters.jobType && filters.jobType !== "all" && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Type: {filters.jobType}
              </span>
            )}
            {filters.remote && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Remote only
              </span>
            )}
            {filters.salaryDisclosed && (
              <span className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full">
                Salary disclosed
              </span>
            )}
            {filters.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-haig-accent/80 text-haig-primary text-xs font-semibold rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </LiquidGlassContainer>
  );
};

export default JobFilters;
